import {
  buildRouteFromTrip,
  estimateJoinerAmount,
  getSegmentCost,
  resolveTripPricingInstant,
} from './pricing';
import { getAvailableRoutesForTrip } from './routeAnalysis';

const DEFAULT_CAPACITY = 6;

const INVALID_ESTIMATE = {
  currentRate: 0,
  projectedRate: 0,
  valid: false,
  seatsFilled: 0,
  progressPct: 0,
  capacity: DEFAULT_CAPACITY,
  currentSeats: 0,
  nextSeats: 0,
  fullRate: 0,
  savingsToNext: 0,
  savingsToFull: 0,
  hasRoomForMore: false,
  priceLadder: [],
};

export function formatSegmentKey(segmentKey) {
  if (!segmentKey) return '';
  const match = segmentKey.match(/^(.+?)To(.+)$/);
  if (!match) return segmentKey;
  return `${match[1]} → ${match[2]}`;
}

export function getAdmittedParticipants(participants = []) {
  return participants.filter((p) => ['authorized', 'paid'].includes(p.payment_status));
}

export function hasAnyPaidAdmittedParticipant(participants = []) {
  return getAdmittedParticipants(participants).some((p) => p.payment_status === 'paid');
}

function occupancyOnSegment(trip, admitted, segmentKey) {
  const route = buildRouteFromTrip(trip);
  const segStartIndex = route.findIndex(
    (city, index) => index < route.length - 1 && `${route[index]}To${route[index + 1]}` === segmentKey,
  );
  if (segStartIndex < 0) return 0;

  let total = 0;
  for (const participant of admitted) {
    const passenger = participant.segment
      ? {
        segment: participant.segment,
        group_size: participant.group_size || 1,
      }
      : null;
    if (!passenger) continue;

    const from = segmentEndpointCity(participant.segment, 'from', trip.direction);
    const to = segmentEndpointCity(participant.segment, 'to', trip.direction, trip);
    if (!from || !to) continue;

    const fromIndex = route.indexOf(from);
    const toIndex = route.indexOf(to);
    if (fromIndex === -1 || toIndex === -1) continue;
    if (fromIndex <= segStartIndex && toIndex > segStartIndex) {
      total += participant.group_size || 1;
    }
  }
  return total;
}

function segmentEndpointCity(segment, end, direction, trip = null) {
  if (direction === 'NairobiToMoshi') {
    if (segment === 'NairobiToArusha') return end === 'from' ? 'Nairobi' : 'Arusha';
    if (segment === 'ArushaToMoshi') return end === 'from' ? 'Arusha' : 'Moshi';
    if (segment === 'NairobiToMoshi') return end === 'from' ? 'Nairobi' : 'Moshi';
  } else if (direction === 'MoshiToNairobi') {
    if (segment === 'MoshiToArusha') return end === 'from' ? 'Moshi' : 'Arusha';
    if (segment === 'ArushaToNairobi') return end === 'from' ? 'Arusha' : 'Nairobi';
    if (segment === 'MoshiToNairobi') return end === 'from' ? 'Moshi' : 'Nairobi';
  }
  if (trip && end === 'from') return trip.pickup_location;
  if (trip && end === 'to') return trip.dropoff_location;
  return null;
}

function boardedSegmentsForJoinSegment(trip, joinSegment) {
  const route = buildRouteFromTrip(trip);
  const from = segmentEndpointCity(joinSegment, 'from', trip.direction, trip);
  const to = segmentEndpointCity(joinSegment, 'to', trip.direction, trip);
  if (!from || !to) return [];

  const segments = [];
  let inPath = false;
  for (let i = 0; i < route.length - 1; i += 1) {
    const segFrom = route[i];
    const segTo = route[i + 1];
    if (segFrom === from) inPath = true;
    if (inPath) segments.push(`${segFrom}To${segTo}`);
    if (segTo === to) break;
  }
  return segments;
}

function pricingAtFromTrip(trip) {
  if (!trip?.created_at) return undefined;
  try {
    return resolveTripPricingInstant(trip);
  } catch {
    return undefined;
  }
}

function buildLegPriceLadder(trip, admitted, segmentKey, capacity) {
  const pricingAt = pricingAtFromTrip(trip);
  const boarded = boardedSegmentsForJoinSegment(trip, segmentKey);
  if (boarded.length === 0) return [];

  const primarySegment = boarded[0];
  const currentOccupancy = Math.max(1, occupancyOnSegment(trip, admitted, primarySegment));
  const hasRoomForMore = currentOccupancy < capacity;

  return Array.from({ length: capacity }, (_, index) => {
    const seats = index + 1;
    let rate;
    if (boarded.length === 1) {
      rate = getSegmentCost(boarded[0], seats, pricingAt);
    } else {
      rate = boarded.reduce((sum, seg) => sum + getSegmentCost(seg, seats, pricingAt), 0);
    }

    const nextSeats = hasRoomForMore ? Math.min(capacity, currentOccupancy + 1) : capacity;
    return {
      seats,
      rate,
      active: seats === currentOccupancy,
      next: hasRoomForMore && seats === nextSeats,
    };
  });
}

export function getJoinLegEstimate(trip, admitted, segmentKey, capacity = DEFAULT_CAPACITY) {
  const seatsFilled = trip.seats_filled ?? 0;
  const progressPct = Math.min(100, (seatsFilled / capacity) * 100);

  try {
    const joinNowRate = estimateJoinerAmount(trip, admitted, segmentKey, 1);
    if (!joinNowRate || joinNowRate <= 0) {
      return { ...INVALID_ESTIMATE, seatsFilled, progressPct, capacity };
    }

    const boarded = boardedSegmentsForJoinSegment(trip, segmentKey);
    const primarySegment = boarded[0] ?? segmentKey;
    const currentOccupancy = Math.max(1, occupancyOnSegment(trip, admitted, primarySegment));
    const hasRoomForMore = seatsFilled < capacity;
    const nextSeats = hasRoomForMore
      ? Math.min(capacity, currentOccupancy + 1)
      : capacity;

    const syntheticJoiner = {
      id: -9000,
      segment: segmentKey,
      group_size: 1,
      payment_status: 'authorized',
    };
    const projectedRate = estimateJoinerAmount(
      trip,
      [...admitted, syntheticJoiner],
      segmentKey,
      1,
      -9001,
    );

    const pricingAt = pricingAtFromTrip(trip);
    const fullRate = boarded.length === 1
      ? getSegmentCost(boarded[0], capacity, pricingAt)
      : boarded.reduce((sum, seg) => sum + getSegmentCost(seg, capacity, pricingAt), 0);

    const savingsToNext = hasRoomForMore && projectedRate < joinNowRate
      ? joinNowRate - projectedRate
      : 0;
    const savingsToFull = joinNowRate > fullRate ? joinNowRate - fullRate : 0;
    const priceLadder = buildLegPriceLadder(trip, admitted, segmentKey, capacity);

    return {
      segmentKey,
      currentRate: joinNowRate,
      projectedRate,
      valid: true,
      seatsFilled,
      progressPct,
      capacity,
      currentSeats: currentOccupancy,
      nextSeats,
      fullRate,
      savingsToNext,
      savingsToFull,
      hasRoomForMore,
      priceLadder,
    };
  } catch {
    return { ...INVALID_ESTIMATE, seatsFilled, progressPct, capacity };
  }
}

/** @deprecated Use getJoinLegEstimate for browse detail */
export function getSegmentPriceEstimate(segmentKey, seatsFilled, capacity = DEFAULT_CAPACITY) {
  const progressPct = Math.min(100, (seatsFilled / capacity) * 100);
  try {
    const currentSeats = Math.max(1, Math.min(capacity, seatsFilled || 1));
    const hasRoomForMore = seatsFilled < capacity;
    const nextSeats = hasRoomForMore
      ? Math.min(capacity, currentSeats + 1)
      : capacity;
    const currentRate = getSegmentCost(segmentKey, currentSeats);
    const projectedRate = getSegmentCost(segmentKey, nextSeats);
    const fullRate = getSegmentCost(segmentKey, capacity);
    const savingsToNext = hasRoomForMore && nextSeats > currentSeats
      ? currentRate - projectedRate
      : 0;
    const savingsToFull = currentSeats < capacity ? currentRate - fullRate : 0;

    const priceLadder = Array.from({ length: capacity }, (_, index) => {
      const seats = index + 1;
      return {
        seats,
        rate: getSegmentCost(segmentKey, seats),
        active: seats === currentSeats,
        next: hasRoomForMore && seats === nextSeats,
      };
    });

    return {
      segmentKey,
      currentRate,
      projectedRate,
      valid: true,
      seatsFilled,
      progressPct,
      capacity,
      currentSeats,
      nextSeats,
      fullRate,
      savingsToNext,
      savingsToFull,
      hasRoomForMore,
      priceLadder,
    };
  } catch {
    return { ...INVALID_ESTIMATE, seatsFilled, progressPct, capacity };
  }
}

export function getBrowseLegOptions(trip) {
  if (!trip?.pickup_location || !trip?.dropoff_location) return [];

  const participants = trip.participants || [];
  const participantsWithCreatorFlag = participants.map((p) => ({
    ...p,
    isCreator: p.user_id === trip.creator_id,
  }));

  let routes = [];
  if (trip.direction) {
    routes = getAvailableRoutesForTrip(trip, participantsWithCreatorFlag);
  }

  if (routes.length === 0) {
    routes = [{ pickup: trip.pickup_location, dropoff: trip.dropoff_location }];
  }

  return routes.map((route) => {
    const value = `${route.pickup}To${route.dropoff}`;
    const isFullRoute =
      route.pickup === trip.pickup_location && route.dropoff === trip.dropoff_location;
    const label = isFullRoute
      ? `${route.pickup} → ${route.dropoff} (Full route)`
      : `${route.pickup} → ${route.dropoff}`;
    return { value, label, pickup: route.pickup, dropoff: route.dropoff, isFullRoute };
  });
}

export function getTripLegEstimates(trip, legs) {
  const capacity = trip.max_participants ?? trip.max_seats ?? DEFAULT_CAPACITY;
  const admitted = getAdmittedParticipants(trip.participants);

  return legs.map((leg) => ({
    ...leg,
    ...getJoinLegEstimate(trip, admitted, leg.value, capacity),
  }));
}

export function getPriceRangeFromLegs(legEstimates) {
  const valid = legEstimates.filter((leg) => leg.valid);
  if (valid.length === 0) return null;
  const rates = valid.map((leg) => leg.currentRate);
  return { min: Math.min(...rates), max: Math.max(...rates) };
}

export function getDefaultLegValue(trip, legOptions) {
  if (!legOptions.length) {
    return `${trip.pickup_location}To${trip.dropoff_location}`;
  }
  const fullRoute = legOptions.find((leg) => leg.isFullRoute);
  return fullRoute?.value ?? legOptions[legOptions.length - 1].value;
}

/** @deprecated Use getJoinLegEstimate / getTripLegEstimates for browse detail */
export function getTripPriceEstimate(trip) {
  const capacity = trip.max_participants ?? trip.max_seats ?? DEFAULT_CAPACITY;
  const seatsFilled = trip.seats_filled ?? 0;
  const segment = `${trip.pickup_location}To${trip.dropoff_location}`;
  return getSegmentPriceEstimate(segment, seatsFilled, capacity);
}

export function hasMixedParticipantSegments(trip) {
  const participants = trip.participants || [];
  if (participants.length < 2) return false;
  const segments = new Set(
    participants.map((p) => p.segment).filter(Boolean)
  );
  return segments.size > 1;
}
